import React, { useState, useEffect } from 'react';
import vector from '../../assets/images/Vector.svg';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL } from "assets/constants/Constants";
import { createUserEnquiryCall, getMetaDataCall } from "lib/axiosUtils";
import ShowToast from 'components/ShowToast';


// Checkmark circle icon — same style as SignupPage
const CheckCircleIcon = () => (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="40" r="39" stroke="#e1d7b7" strokeWidth="1.2" />
        <path d="M25 40l10 10 20-20" stroke="#e1d7b7" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// ------------------------------------------------------------
// Mixpanel helper — safe no-op if the snippet hasn't loaded yet
// (e.g. slow network, ad blocker) so tracking never breaks the form.
// The <script> snippet itself lives in public/index.html and only
// needs to be added once for the whole app.
// ------------------------------------------------------------
function trackEvent(eventName, props = {}) {
    if (typeof window !== 'undefined' && window.mixpanel && typeof window.mixpanel.track === 'function') {
        window.mixpanel.track(eventName, props);
    }
}

const ContactPage = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        workEmail: '',
        companyName: '',
        domains: [],
        extraInfo: '',
    });
    const [status, setStatus] = useState(''); // '' | 'submitting' | 'success'
    const [countdown, setCountdown] = useState(10);
    const [errors, setErrors] = useState({});
    const [domainOptions, setDomainOptions] = useState([]);

    const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const metadata = await Promise.all([
                    getMetaDataCall({}, {}),
                ]);
                const domains = Object.keys(
                    metadata[0]?.data?.result?.[0]?.meta_data?.process_data?.classification_fields || {}
                );
                setDomainOptions(domains);
            } catch (error) {
                console.error('Failed to load signup metadata', error);
            } finally {
            }
        };
        loadInitialData();
        trackEvent('Contact Page Viewed');
    }, []);


    const handleChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const toggleDomain = (domain) => {
        setFormData((prev) => ({
            ...prev,
            domains: prev.domains.includes(domain)
                ? prev.domains.filter((d) => d !== domain)
                : [...prev.domains, domain],
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required.';
        if (!formData.workEmail.trim()) {
            newErrors.workEmail = 'Work email is required.';
        } else if (!EMAIL_RE.test(formData.workEmail.trim())) {
            newErrors.workEmail = 'Please enter a valid email address.';
        }
        if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required.';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            trackEvent('Contact Form Validation Failed');
            return;
        }
        setStatus('submitting');
        trackEvent('Contact Form Submitted', {
            companyName: formData.companyName.trim(),
            domains: formData.domains,
        });

        try {
            const userEmail = formData.workEmail.trim();

            const requestData = {
                userFullName: formData.fullName,
                userEmailId: userEmail,
                userCompanyName: formData.companyName.trim(),
                userDomains: formData.domains,
                userExtraInfo: formData.extraInfo
            };

            const userResponse = await createUserEnquiryCall({}, requestData);

            if (userResponse?.statusCode === 200) {
                ShowToast(
                    'Your inquiry has been submitted successfully.',
                    TYPE_SUCCESS,
                    INTERVAL,
                    "",
                    ""
                );
                trackEvent('Contact Form Success');
                if (window.mixpanel && typeof window.mixpanel.identify === 'function') {
                    window.mixpanel.identify(userEmail);
                    window.mixpanel.people.set({
                        $email: userEmail,
                        $name: formData.fullName.trim(),
                        companyName: formData.companyName.trim(),
                    });
                }
                setTimeout(() => setStatus('success'), 800);
                // setFormData(INITIAL_FORM);
            } else {
                throw new Error(userResponse?.message || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            const message = error.message || 'Something went wrong. Please try again.';
            setStatus('error');
            trackEvent('Contact Form Failed', { reason: message });
            ShowToast(message, TYPE_FAIL, INTERVAL, "", "");
        }
    };

    const redirectHome = () => {
        window.location.href = '/';
    };

    // Auto-redirect after 10 seconds on success
    useEffect(() => {
        if (status !== 'success') return;
        setCountdown(10);
        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    redirectHome();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [status]);

    // ────────────────────────────────────────────────────────────
    // Success view — mirrors SignupPage "Your request is in"
    // ────────────────────────────────────────────────────────────
    if (status === 'success') {
        return (
            <section className="signup-page">
                <div className="signup-card signup-card--success" style={{ position: 'relative' }}>

                    {/* Close button — top-right corner */}
                    <button
                        type="button"
                        className="personalize-close-btn"
                        onClick={redirectHome}
                        aria-label="Close"
                    >
                        &times;
                    </button>

                    {/* Left — confirmation panel */}
                    <div className="signup-panel-left">
                        <div className="signup-panel-left__intro">
                            <span className="signup-panel-left__check">
                                <CheckCircleIcon />
                            </span>
                            <h2 className="signup-panel-left__heading_contact">
                                Thanks for reaching out!<br />We'll get back to you shortly
                            </h2>
                        </div>
                    </div>

                    {/* Right — confirmation message, centred */}
                    <div className="signup-panel-right signup-panel-right--success">
                        <div
                            className="signup-panel-right__header"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                width: '100%',
                                height: '100%',
                                gap: '24px',
                            }}
                        >
                            <h1
                                className="signup-panel-right__title"
                                style={{ textAlign: 'center' }}
                            >
                                Turn chaos into opportunity
                            </h1>
                            <p
                                className="signup-panel-right__subtitle"
                                style={{ textAlign: 'center' }}
                            >
                                Hence early access is limited to 100 enterprises, enabling us to provide a more personalized experience
                            </p>
                            <p style={{ fontSize: '13px', color: '#999', marginTop: '8px' }}>
                                Redirecting to home in {countdown}s…
                            </p>
                        </div>
                    </div>

                </div>
            </section>
        );
    }

    // ────────────────────────────────────────────────────────────
    // Form view
    // ────────────────────────────────────────────────────────────
    return (
        <section className="signup-page">
            <div className="signup-card">

                {/* ── Left panel ── */}
                <div className="signup-panel-left" style={{ justifyContent: 'center' }}>

                    {/* H2 — large heading */}
                    <h2 className="signup-panel-left__heading" style={{ marginTop: '50%' }}>
                        Turn chaos into<br />
                        opportunity
                    </h2>

                    {/* H3 + subtext block */}
                    <div className="signup-panel-left__intro">
                        <h3 className="signup-panel-left__intro-heading">

                        </h3>
                        <p className="signup-panel-left__subtext">
                        </p>
                    </div>

                    <div className="feature-row" style={{ marginTop: 'auto' }}>
                        <div className="feature-row__icon">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z"
                                    fill="#E1D7B7"
                                />
                            </svg>
                        </div>
                        <div className="feature-row__body">
                            <p className="feature-row__desc">
                                contact@millionai.ai
                            </p>
                        </div>
                    </div>

                </div>

                {/* ── Right panel ── */}
                <div className="signup-panel-right">

                    <div className="signup-panel-right__header">
                        <h1 className="signup-panel-right__title">Contact us</h1>
                    </div>

                    <form className="contactus-form" onSubmit={handleSubmit} noValidate>

                        {/* Full Name */}
                        <div className="contactform-field">
                            <label className="contactform-field__label" htmlFor="fullName">
                                Full Name <span className="contactform-field__required">*</span>
                            </label>
                            <input
                                id="fullName"
                                type="text"
                                className="contactform-field__input"
                                placeholder="..."
                                value={formData.fullName}
                                onChange={handleChange('fullName')}
                            />
                            {errors.fullName && <span className="contactform-field__error">{errors.fullName}</span>}
                        </div>

                        {/* Work Email */}
                        <div className="contactform-field">
                            <label className="contactform-field__label" htmlFor="workEmail">
                                Work Email <span className="contactform-field__required">*</span>
                            </label>
                            <input
                                id="workEmail"
                                type="email"
                                className="contactform-field__input"
                                placeholder="name@example.com"
                                value={formData.workEmail}
                                onChange={handleChange('workEmail')}
                            />
                            {errors.workEmail && <span className="contactform-field__error">{errors.workEmail}</span>}
                        </div>

                        {/* Company Name */}
                        <div className="contactform-field">
                            <label className="contactform-field__label" htmlFor="companyName">
                                Company Name <span className="contactform-field__required">*</span>
                            </label>
                            <input
                                id="companyName"
                                type="text"
                                className="contactform-field__input"
                                placeholder="..."
                                value={formData.companyName}
                                onChange={handleChange('companyName')}
                            />
                            {errors.companyName && <span className="contactform-field__error">{errors.companyName}</span>}
                        </div>

                        {/* Domains to focus on */}
                        <div className="contactform-field">
                            <label className="contactform-field__label">Domains to focus on</label>
                            <div className="domain-pills">
                                {domainOptions.map((domain) => {
                                    const selected = formData.domains.includes(domain);
                                    return (
                                        <button
                                            type="button"
                                            key={domain}
                                            className={`domain-pill${selected ? ' domain-pill--selected' : ''}`}
                                            onClick={() => toggleDomain(domain)}   // ← wrap in arrow function
                                            aria-pressed={selected}
                                        >
                                            {domain}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Extra info */}
                        <div className="contactform-field">
                            <label className="contactform-field__label" htmlFor="extraInfo">
                                Is there anything else you would like us to know?
                            </label>
                            <input
                                id="extraInfo"
                                type="text"
                                className="contactform-field__input"
                                placeholder="..."
                                value={formData.extraInfo}
                                onChange={handleChange('extraInfo')}
                            />
                        </div>

                        {/* Submit */}
                        <div className="contactus-form__actions">
                            <button type="submit" className="cta-button">
                                Submit
                                <img src={vector} alt="" className="cta-button__icon" />
                            </button>

                            <p className="signup-consent">
                                I agree that million ai stores my contact details for the purpose of scheduled meetings and follow-ups.
                                By submitting the form, you acknowledge our{' '}
                                <a href="/pages/PrivacyNotice.html">Privacy Policy</a>.
                                You can revoke this consent at any time.
                            </p>
                        </div>

                    </form>
                </div>

            </div>
        </section >
    );
};

export default ContactPage;
